import React, { useEffect, useState } from 'react';
import {
  BarChart3,
  Users,
  UserPlus2,
  UserMinus2,
  Cake,
  CalendarDays,
  Clock,
} from 'lucide-react';
import { usePageHeader } from '../../../contexts/PageHeaderContext';
import { useManagerDashboard } from '../../../hooks/manager_portal_hooks';
import TeamHeadcountModal from './helpers/TeamHeadcountModal';
import NewJoinersModal from './helpers/NewJoinersModal';
import ExitingEmployeesModal from './helpers/ExitingEmployeesModal';
import TeamBirthdaysModal from './helpers/TeamBirthdaysModal';
import OnLeaveTodayModal from './helpers/OnLeaveTodayModal';
import UpcomingLeavesModal from './helpers/UpcomingLeavesModal';

const ManagerDashboard = () => {
  const { setHeader } = usePageHeader();
  const { metrics, isLoading, error } = useManagerDashboard();
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [showNewJoinersModal, setShowNewJoinersModal] = useState(false);
  const [showExitingModal, setShowExitingModal] = useState(false);
  const [showBirthdaysModal, setShowBirthdaysModal] = useState(false);
  const [showOnLeaveModal, setShowOnLeaveModal] = useState(false);
  const [showUpcomingModal, setShowUpcomingModal] = useState(false);

  useEffect(() => {
    setHeader({
      icon: BarChart3,
      title: 'Manager Dashboard',
      subtitle: 'High-level overview of your team.',
    });
    return () => setHeader({ icon: null, title: '', subtitle: '' });
  }, [setHeader]);

  // Show a single dashboard-wide spinner until every metric hook has resolved,
  // so cards appear together with real data rather than flickering placeholders.
  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">
        <div className="flex flex-col items-center justify-center py-24">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-indigo-500"></div>
          <p className="mt-4 text-sm text-gray-600">Loading your dashboard…</p>
        </div>
      </div>
    );
  }

  // If the aggregate metrics query failed, surface a single retryable message.
  if (error) {
    return (
      <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <p className="text-sm text-red-600 font-medium">
            We couldn’t load your dashboard.
          </p>
          <p className="mt-1 text-sm text-gray-500">
            Please refresh the page or try again in a moment.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
        <button
          type="button"
          onClick={() => setShowTeamModal(true)}
          className="bg-white rounded-lg shadow-md p-4 sm:p-5 flex items-center gap-3 sm:gap-4 text-left w-full hover:shadow-lg hover:ring-2 hover:ring-indigo-200 transition-all focus:outline-none focus:ring-2 focus:ring-indigo-400"
          aria-label="View team members"
        >
          <div className="p-2.5 sm:p-3 rounded-full bg-indigo-50 flex-shrink-0">
            <Users className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-600" />
          </div>
          <div>
            <p className="text-xs sm:text-sm text-gray-500">Team headcount</p>
            <p className="text-xl sm:text-2xl font-bold text-gray-900">
              {metrics.headcount}
            </p>
          </div>
        </button>

        <button
          type="button"
          onClick={() => setShowNewJoinersModal(true)}
          className="bg-white rounded-lg shadow-md p-4 sm:p-5 flex items-center gap-3 sm:gap-4 text-left w-full hover:shadow-lg hover:ring-2 hover:ring-green-200 transition-all focus:outline-none focus:ring-2 focus:ring-green-400"
          aria-label="View new joiners in my team"
        >
          <div className="p-2.5 sm:p-3 rounded-full bg-green-50 flex-shrink-0">
            <UserPlus2 className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
          </div>
          <div>
            <p className="text-xs sm:text-sm text-gray-500">My Team – New joiners (last 15 days)</p>
            <p className="text-xl sm:text-2xl font-bold text-gray-900">
              {metrics.newJoiners}
            </p>
          </div>
        </button>

        <button
          type="button"
          onClick={() => setShowExitingModal(true)}
          className="bg-white rounded-lg shadow-md p-4 sm:p-5 flex items-center gap-3 sm:gap-4 text-left w-full hover:shadow-lg hover:ring-2 hover:ring-red-200 transition-all focus:outline-none focus:ring-2 focus:ring-red-400"
          aria-label="View employees leaving SHP from my team in the next 30 days"
        >
          <div className="p-2.5 sm:p-3 rounded-full bg-red-50 flex-shrink-0">
            <UserMinus2 className="w-5 h-5 sm:w-6 sm:h-6 text-red-600" />
          </div>
          <div>
            <p className="text-xs sm:text-sm text-gray-500">My Team – Employees leaving SHP (in next 30 days)</p>
            <p className="text-xl sm:text-2xl font-bold text-gray-900">
              {metrics.exitingEmployees}
            </p>
          </div>
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <button
          type="button"
          onClick={() => setShowBirthdaysModal(true)}
          className="bg-white rounded-lg shadow-md p-4 sm:p-5 flex items-center gap-3 sm:gap-4 text-left w-full hover:shadow-lg hover:ring-2 hover:ring-pink-200 transition-all focus:outline-none focus:ring-2 focus:ring-pink-400"
          aria-label="View team member birthdays this month"
        >
          <div className="p-2.5 sm:p-3 rounded-full bg-pink-50 flex-shrink-0">
            <Cake className="w-5 h-5 sm:w-6 sm:h-6 text-pink-600" />
          </div>
          <div>
            <p className="text-xs sm:text-sm text-gray-500">Team member birthdays this month</p>
            <p className="text-xl sm:text-2xl font-bold text-gray-900">
              {metrics.birthdaysThisMonth}
            </p>
          </div>
        </button>

        <button
          type="button"
          onClick={() => setShowOnLeaveModal(true)}
          className="bg-white rounded-lg shadow-md p-4 sm:p-5 flex items-center gap-3 sm:gap-4 text-left w-full hover:shadow-lg hover:ring-2 hover:ring-yellow-200 transition-all focus:outline-none focus:ring-2 focus:ring-yellow-400"
          aria-label="View team members on leave today"
        >
          <div className="p-2.5 sm:p-3 rounded-full bg-yellow-50 flex-shrink-0">
            <CalendarDays className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-600" />
          </div>
          <div>
            <p className="text-xs sm:text-sm text-gray-500">On leave today</p>
            <p className="text-xl sm:text-2xl font-bold text-gray-900">
              {metrics.onLeaveToday}
            </p>
          </div>
        </button>

        <button
          type="button"
          onClick={() => setShowUpcomingModal(true)}
          className="bg-white rounded-lg shadow-md p-4 sm:p-5 flex items-center gap-3 sm:gap-4 md:col-span-2 text-left w-full hover:shadow-lg hover:ring-2 hover:ring-blue-200 transition-all focus:outline-none focus:ring-2 focus:ring-blue-400"
          aria-label="View staff on leave in the next 30 days"
        >
          <div className="p-2.5 sm:p-3 rounded-full bg-blue-50 flex-shrink-0">
            <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
          </div>
          <div>
            <p className="text-xs sm:text-sm text-gray-500">Staff on leave (in next 30 days)</p>
            <p className="text-xl sm:text-2xl font-bold text-gray-900">
              {metrics.upcomingLeaves}
            </p>
          </div>
        </button>
      </div>

      {showTeamModal && (
        <TeamHeadcountModal onClose={() => setShowTeamModal(false)} />
      )}

      {showNewJoinersModal && (
        <NewJoinersModal onClose={() => setShowNewJoinersModal(false)} />
      )}

      {showExitingModal && (
        <ExitingEmployeesModal onClose={() => setShowExitingModal(false)} />
      )}

      {showBirthdaysModal && (
        <TeamBirthdaysModal onClose={() => setShowBirthdaysModal(false)} />
      )}

      {showOnLeaveModal && (
        <OnLeaveTodayModal onClose={() => setShowOnLeaveModal(false)} />
      )}

      {showUpcomingModal && (
        <UpcomingLeavesModal onClose={() => setShowUpcomingModal(false)} />
      )}
    </div>
  );
};

export default ManagerDashboard;


